/*
  Cookie wall: keeps the page behind Shopify's cookie banner out of reach for as long as the banner is up.

  The banner itself, its buttons and the consent they store are Shopify's (see assets/cookie-banner.css). This only
  follows the banner: while it is visible, <html> gets .cookie-wall-open (scroll lock, chat button hidden) and every
  other part of the page is made inert, so the keyboard and screen readers cannot reach the page behind the blur
  either. Nothing here shows, hides or answers the banner.

  Only runs with html.cookie-wall (snippets/cookie-banner-class.liquid): not on policy pages, not in the theme editor,
  and not when the wall is switched off in Theme settings > Cookie banner.
*/

const root = document.documentElement;

/*
  Two-tone titles: the first word of the banner and preferences titles goes in a span that assets/cookie-banner.css sets
  in the light weight ("Cookie instellingen"). Only the markup of the title changes, the text stays Shopify's. A title of
  one word is left as it is. Stops watching once both titles are done.
*/
{
  const TITLE_IDS = ['shopify-pc__banner__body-title', 'shopify-pc__prefs__header-title'];
  let queued = false;

  const splitTitle = (title) => {
    if (!title || title.querySelector('.cookie-heading__light')) {
      return !!title;
    }

    const text = title.textContent.trim();
    const space = text.search(/\s/);

    if (space < 1) {
      return true;
    }

    const light = document.createElement('span');
    light.className = 'cookie-heading__light';
    light.textContent = text.slice(0, space);
    title.replaceChildren(light, text.slice(space));
    return true;
  };

  const splitTitles = () => TITLE_IDS.map((id) => splitTitle(document.getElementById(id))).every(Boolean);

  const titleObserver = new MutationObserver(() => {
    if (queued) {
      return;
    }

    queued = true;
    requestAnimationFrame(() => {
      queued = false;

      if (splitTitles()) {
        titleObserver.disconnect();
      }
    });
  });

  // Shopify may have added one or both before this script runs
  if (!splitTitles()) {
    titleObserver.observe(document.body, { childList: true, subtree: true });
  }
}

/*
  Cards and labels: the banner card and the preferences dialog get the theme's white colour scheme, which is where the
  buttons take their colours from (assets/cookie-banner.css styles the buttons by id), and "Meer info" and "Alles
  accepteren" replace Shopify's labels, from the theme's translations (locales/*.json > cookie_banner, put on <html> by
  the layouts). Shopify re-renders parts of both dialogs, so this is checked again after every change and only
  touches what is missing.
*/
{
  const SCHEME = ['color-scheme', 'color-scheme--scheme-2'];
  const CARDS = ['#shopify-pc__banner .shopify-pc__banner__wrapper', '#shopify-pc__prefs__dialog'];
  const LABELS = [
    ['shopify-pc__banner__btn-accept', root.dataset.cookieAcceptAll],
    ['shopify-pc__banner__btn-manage-prefs', root.dataset.cookieMoreInfo]
  ];

  let queued = false;

  /*
    Switches on by default (store owner's choice, made knowing that the Dutch privacy regulator does not count
    pre-ticked boxes as consent): when the preferences open for a visitor who has not chosen yet, the optional switches
    are turned on through Shopify's own checkboxes. A visitor with a saved choice sees that choice.
  */
  const OPTIONAL = ['preferences', 'marketing', 'analytics'];

  const isUndecided = () => {
    const consent = window.Shopify?.customerPrivacy?.currentVisitorConsent?.();
    return !!consent && OPTIONAL.every((key) => consent[key] !== 'yes' && consent[key] !== 'no');
  };

  // Shopify opens and closes the preferences with an inline display, so only that element's style is watched
  let watchedPrefs = null;
  const prefsObserver = new MutationObserver(() => switchOnByDefault());

  const switchOnByDefault = () => {
    const prefs = document.getElementById('shopify-pc__prefs');

    if (prefs && prefs !== watchedPrefs) {
      prefsObserver.disconnect();
      prefsObserver.observe(prefs, { attributes: true, attributeFilter: ['style'] });
      watchedPrefs = prefs;
    }

    if (!prefs || getComputedStyle(prefs).display === 'none') {
      if (prefs) {
        delete prefs.dataset.cookieDefaults;
      }

      return;
    }

    if (prefs.dataset.cookieDefaults || !isUndecided()) {
      return;
    }

    prefs.dataset.cookieDefaults = 'on';
    OPTIONAL.forEach((key) => {
      const input = document.getElementById(`shopify-pc__prefs__${key}-input`);

      if (input && !input.checked) {
        input.click();
      }
    });
  };

  /*
    Banner text from the theme's translations (locales/*.json > cookie_banner: title, text_1, text_2). Left to Shopify
    when empty (English has none yet). The title keeps the two-tone split; the body becomes two paragraphs and keeps
    Shopify's privacy policy link at the end.
  */
  const replaceBannerText = () => {
    // getAttribute, because dataset does not camel-case a dash before a digit (data-cookie-text-1)
    const title = root.dataset.cookieTitle;
    const text1 = root.getAttribute('data-cookie-text-1');
    const text2 = root.getAttribute('data-cookie-text-2');
    const heading = document.getElementById('shopify-pc__banner__body-title');
    const body = heading?.parentElement;

    if (title && heading && heading.dataset.cookieText !== title) {
      const space = title.search(/\s/);
      const light = document.createElement('span');
      light.className = 'cookie-heading__light';
      light.textContent = space > 0 ? title.slice(0, space) : title;
      heading.replaceChildren(light, space > 0 ? title.slice(space) : '');
      heading.dataset.cookieText = title;
    }

    if (text1 && body && !body.querySelector('.cookie-banner__text')) {
      const original = body.querySelector('p');
      const link = original?.querySelector('a');
      const paragraphs = [text1, text2].filter(Boolean).map((text) => {
        const paragraph = document.createElement('p');
        paragraph.className = 'cookie-banner__text';
        paragraph.textContent = text;
        return paragraph;
      });

      if (link) {
        paragraphs[paragraphs.length - 1].append(' ', link);
      }

      if (original) {
        original.replaceWith(...paragraphs);
      } else {
        body.append(...paragraphs);
      }
    }
  };

  const apply = () => {
    switchOnByDefault();
    replaceBannerText();

    CARDS.forEach((selector) => {
      const card = document.querySelector(selector);

      if (card && !card.classList.contains(SCHEME[1])) {
        card.classList.add(...SCHEME);
      }
    });

    LABELS.forEach(([id, label]) => {
      // "Voorkeuren beheren" has an inner <span>, accept has its text directly
      const target = document.getElementById(id)?.querySelector('span') || document.getElementById(id);

      if (label && target && target.textContent !== label && !target.querySelector('.hover-band')) {
        target.textContent = label;
      }
    });
  };

  new MutationObserver(() => {
    if (queued) {
      return;
    }

    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      apply();
    });
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });

  apply();
}

if (root.classList.contains('cookie-wall')) {
  const BANNER_ID = 'shopify-pc__banner';
  const PREFERENCES_ID = 'shopify-pc__prefs';
  const OPEN_CLASS = 'cookie-wall-open';

  // Only what this script made inert is released again, so parts the theme made inert itself stay that way
  const inerted = new Set();
  let watchedBanner = null;

  const bannerObserver = new MutationObserver(() => sync());

  const isShown = (element) => !!element && getComputedStyle(element).display !== 'none';

  const release = () => {
    inerted.forEach((element) => {
      element.inert = false;
    });
    inerted.clear();
  };

  function sync() {
    const banner = document.getElementById(BANNER_ID);

    // Shopify adds the banner after the page has loaded and shows or hides it with an inline display
    if (banner && banner !== watchedBanner) {
      bannerObserver.disconnect();
      bannerObserver.observe(banner, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      watchedBanner = banner;
    }

    const open = isShown(banner);
    root.classList.toggle(OPEN_CLASS, open);

    if (!open) {
      release();
      return;
    }

    const preferences = document.getElementById(PREFERENCES_ID);

    // Runs again whenever something is added to <body>, so late arrivals (chat, popups) end up behind the wall too
    for (const element of document.body.children) {
      if (element === banner || element === preferences || element.inert) {
        continue;
      }

      element.inert = true;
      inerted.add(element);
    }

    // Focus moves into the dialog once, to the dialog itself and not to a button, so no choice is preselected
    const active = document.activeElement;

    if (!banner.contains(active) && !(preferences && preferences.contains(active))) {
      banner.setAttribute('tabindex', '-1');
      banner.focus({ preventScroll: true });
    }
  }

  new MutationObserver(() => sync()).observe(document.body, { childList: true });
  sync();
}
